import React, { useState } from 'react';
import { Drawer, Button, Tabs, Typography, Space, Tag, Divider, Card } from 'antd';
import { BugOutlined, ClearOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDebug } from '../../contexts/DebugContext';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text } = Typography;

const GlobalDebugPanel: React.FC = () => {
  const { debugMode, toggleDebugMode, getAllDebugInfo, clearDebugInfo } = useDebug();
  const [visible, setVisible] = useState(false);
  const debugInfo = getAllDebugInfo();
  const location = useLocation();

  // 检查是否有权限访问系统配置（用于判断是否显示后端控制提示）
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  // 检查管理员权限
  React.useEffect(() => {
    // 在登录页面或注册页面时不检查管理员权限
    const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
    if (isAuthPage) {
      setHasAdminAccess(false);
      return;
    }

    const checkAdminAccess = async () => {
      try {
        // 先检查用户角色，而不是直接访问管理员API
        const userResponse = await api.get('/auth/me');
        const user = userResponse.data;
        
        if (user.role === 'admin') {
          setHasAdminAccess(true);
        } else {
          setHasAdminAccess(false);
        }
      } catch (error: any) {
        console.warn('检查管理员权限失败:', error);
        setHasAdminAccess(false);
      }
    };
    checkAdminAccess();
  }, [location.pathname]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('调试信息已复制到剪贴板');
    });
  };

  const downloadDebugInfo = () => {
    const dataStr = JSON.stringify(debugInfo, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `debug-info-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderDebugInfo = (key: string, info: any) => {
    return (
      <Card key={key} size="small" className="mb-2">
        <div className="flex justify-between items-center mb-2">
          <Text strong>{key}</Text>
          <Tag color="blue">{info.timestamp}</Tag>
        </div>
        <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-40 border">
          {JSON.stringify(info.data, null, 2)}
        </pre>
        <div className="mt-2">
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(JSON.stringify(info.data, null, 2))}
          >
            复制
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <>
      {/* 调试模式切换按钮 - 只有在调试模式启用时才显示 */}
      {debugMode && (
        <Button
          type="primary"
          icon={<BugOutlined />}
          onClick={toggleDebugMode}
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1000,
            borderRadius: '50%',
            width: 50,
            height: 50
          }}
          title={`调试模式开启 ${hasAdminAccess ? '(可在后台设置中关闭)' : '(Ctrl+Shift+D切换)'}`}
        />
      )}

      {/* 调试面板 */}
      <Drawer
        title={
          <div className="flex justify-between items-center">
            <Space>
              <BugOutlined />
              <span>全局调试面板</span>
              <Tag color="green">开启</Tag>
              {hasAdminAccess && (
                <Tag color="blue">后台控制</Tag>
              )}
            </Space>
            <Space>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={downloadDebugInfo}
                disabled={Object.keys(debugInfo).length === 0}
              >
                导出
              </Button>
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={clearDebugInfo}
                disabled={Object.keys(debugInfo).length === 0}
              >
                清空
              </Button>
            </Space>
          </div>
        }
        placement="right"
        width={600}
        open={visible}
        onClose={() => setVisible(false)}
        mask={false}
      >
        <div className="space-y-4">
          <Card size="small">
            <Title level={5}>调试模式状态</Title>
            <Space direction="vertical" className="w-full">
              <div className="flex justify-between">
                <Text>调试模式:</Text>
                <Tag color="green">开启</Tag>
              </div>
              <div className="flex justify-between">
                <Text>控制方式:</Text>
                <Tag color={hasAdminAccess ? "blue" : "orange"}>
                  {hasAdminAccess ? "后台控制" : "本地控制"}
                </Tag>
              </div>
              <div className="flex justify-between">
                <Text>调试信息数量:</Text>
                <Tag>{Object.keys(debugInfo).length}</Tag>
              </div>
              {hasAdminAccess ? (
                <div className="flex justify-between">
                  <Text>管理提示:</Text>
                  <Tag color="blue">可在后台设置中关闭</Tag>
                </div>
              ) : (
                <div className="flex justify-between">
                  <Text>快捷键:</Text>
                  <Tag>Ctrl+Shift+D</Tag>
                </div>
              )}
            </Space>
          </Card>

          <Divider />

          <Tabs 
            defaultActiveKey="all"
            items={[
              {
                key: 'all',
                label: '所有信息',
                children: (
                  <div className="space-y-2">
                    {Object.keys(debugInfo).length === 0 ? (
                      <Text type="secondary">暂无调试信息</Text>
                    ) : (
                      Object.entries(debugInfo).map(([key, info]) => renderDebugInfo(key, info))
                    )}
                  </div>
                )
              },
              {
                key: 'api',
                label: 'API请求',
                children: (
                  <div className="space-y-2">
                    {Object.entries(debugInfo)
                      .filter(([key]) => key.includes('api') || key.includes('request'))
                      .map(([key, info]) => renderDebugInfo(key, info))}
                  </div>
                )
              },
              {
                key: 'form',
                label: '表单数据',
                children: (
                  <div className="space-y-2">
                    {Object.entries(debugInfo)
                      .filter(([key]) => key.includes('form') || key.includes('step'))
                      .map(([key, info]) => renderDebugInfo(key, info))}
                  </div>
                )
              },
              {
                key: 'error',
                label: '错误信息',
                children: (
                  <div className="space-y-2">
                    {Object.entries(debugInfo)
                      .filter(([key]) => key.includes('error') || key.includes('exception'))
                      .map(([key, info]) => renderDebugInfo(key, info))}
                  </div>
                )
              }
            ]}
          />
        </div>
      </Drawer>

      {/* 调试面板打开按钮 */}
      {debugMode && (
        <Button
          type="primary"
          icon={<BugOutlined />}
          onClick={() => setVisible(true)}
          style={{
            position: 'fixed',
            top: 80,
            right: 20,
            zIndex: 1000
          }}
          title="打开调试面板"
        >
          调试面板
        </Button>
      )}
    </>
  );
};

export default GlobalDebugPanel;
